import type { AppTheme } from '../hooks/useAppTheme';

const HEX6 = /^#[0-9A-Fa-f]{6}$/;

export type SlideDisplayColorRole = 'foreground' | 'stroke' | 'background';

function normalizeHex(raw: string): string | null {
  const s = String(raw ?? '').trim();
  if (!HEX6.test(s)) return null;
  return s.toLowerCase();
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.slice(1);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  return `#${[clamp(r), clamp(g), clamp(b)].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      default:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h /= 360;
  s /= 100;
  l /= 100;
  if (s === 0) {
    const v = l * 255;
    return { r: v, g: v, b: v };
  }
  const hue2rgb = (p: number, q: number, t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: hue2rgb(p, q, h + 1 / 3) * 255,
    g: hue2rgb(p, q, h) * 255,
    b: hue2rgb(p, q, h - 1 / 3) * 255,
  };
}

function relativeLuminance(r: number, g: number, b: number): number {
  const linear = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}

/** 저장 색은 그대로 두고, 다크모드 슬라이드 표시용으로만 보정 */
export function mapSlideDisplayColor(
  hex: string,
  theme: AppTheme,
  role: SlideDisplayColorRole = 'foreground'
): string {
  if (theme === 'light') return hex;
  const normalized = normalizeHex(hex);
  if (!normalized) return hex;

  const { r, g, b } = hexToRgb(normalized);
  const lum = relativeLuminance(r, g, b);
  const { h, s, l } = rgbToHsl(r, g, b);

  if (role === 'foreground') {
    if (lum >= 0.38) return normalized;
    const targetL = Math.max(78, 96 - l * 0.45);
    const targetS = s < 8 ? 0 : Math.min(s, 55);
    const rgb = hslToRgb(h, targetS, targetL);
    return rgbToHex(rgb.r, rgb.g, rgb.b);
  }

  if (role === 'stroke') {
    if (lum >= 0.3) return normalized;
    const targetL = Math.max(62, 90 - l * 0.35);
    const targetS = s < 8 ? 0 : Math.min(s, 45);
    const rgb = hslToRgb(h, targetS, targetL);
    return rgbToHex(rgb.r, rgb.g, rgb.b);
  }

  if (lum <= 0.12) {
    const targetL = Math.max(24, l + 20);
    const rgb = hslToRgb(h, Math.min(s, 28), targetL);
    return rgbToHex(rgb.r, rgb.g, rgb.b);
  }
  if (lum >= 0.9) {
    const targetL = Math.min(l, 86);
    const rgb = hslToRgb(h, Math.max(0, s * 0.25), targetL);
    return rgbToHex(rgb.r, rgb.g, rgb.b);
  }
  return normalized;
}

export function createSlideDisplayColorMappers(theme: AppTheme) {
  return {
    theme,
    fg: (hex: string) => mapSlideDisplayColor(hex, theme, 'foreground'),
    stroke: (hex: string) => mapSlideDisplayColor(hex, theme, 'stroke'),
    bg: (hex: string) => mapSlideDisplayColor(hex, theme, 'background'),
  };
}

export function slideDisplayHex(raw: string | undefined, fallback: string): string {
  return raw?.match(/^#/) ? raw : fallback;
}
